import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Info } from "lucide-react";

/**
 * NIPOST zone = the first segment of a resolved postcode (e.g. "LA" out of
 * "LA-11-A13-DF-02"), one per state + FCT. We don't hardcode this anywhere
 * in the enrichment/analytics logic — zones are whatever NIPOST actually
 * returns — this table is reference-only, to help an operator read the
 * zone codes shown in the UI. Only Lagos ("LA") has been confirmed live.
 */
const ZONE_MAP: { code: string; state: string }[] = [
  { code: "AB", state: "Abia" },
  { code: "AD", state: "Adamawa" },
  { code: "AK", state: "Akwa Ibom" },
  { code: "AN", state: "Anambra" },
  { code: "BA", state: "Bauchi" },
  { code: "BY", state: "Bayelsa" },
  { code: "BE", state: "Benue" },
  { code: "BO", state: "Borno" },
  { code: "CR", state: "Cross River" },
  { code: "DT", state: "Delta" },
  { code: "EB", state: "Ebonyi" },
  { code: "ED", state: "Edo" },
  { code: "EK", state: "Ekiti" },
  { code: "EN", state: "Enugu" },
  { code: "FC", state: "FCT (Abuja)" },
  { code: "GM", state: "Gombe" },
  { code: "IM", state: "Imo" },
  { code: "JG", state: "Jigawa" },
  { code: "KD", state: "Kaduna" },
  { code: "KN", state: "Kano" },
  { code: "KT", state: "Katsina" },
  { code: "KB", state: "Kebbi" },
  { code: "KG", state: "Kogi" },
  { code: "KW", state: "Kwara" },
  { code: "LA", state: "Lagos" },
  { code: "NA", state: "Nasarawa" },
  { code: "NG", state: "Niger" },
  { code: "OG", state: "Ogun" },
  { code: "ON", state: "Ondo" },
  { code: "OS", state: "Osun" },
  { code: "OY", state: "Oyo" },
  { code: "PL", state: "Plateau" },
  { code: "RV", state: "Rivers" },
  { code: "SO", state: "Sokoto" },
  { code: "TA", state: "Taraba" },
  { code: "YB", state: "Yobe" },
  { code: "ZM", state: "Zamfara" },
];

export function ZoneMappingDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6" title="What do zone codes mean?">
          <Info className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>NIPOST zone codes</DialogTitle>
          <DialogDescription>
            Zone = the first segment of a resolved postcode (e.g. "LA" in "LA-11-A13-DF-02"), one per state + FCT.
            Only Lagos ("LA") has been confirmed against the live API so far — others are reference only.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="p-2 text-left font-medium">Code</th>
                <th className="p-2 text-left font-medium">State</th>
              </tr>
            </thead>
            <tbody>
              {ZONE_MAP.map((z) => (
                <tr key={z.code} className="border-t">
                  <td className="p-2 font-mono text-xs">{z.code}</td>
                  <td className="p-2">{z.state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
